import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { compose } from 'react-apollo';
import GraphQLClient from '@api/graphql/GraphQLClient';
import * as Queries from '@api/graphql/queries';
import { connect } from 'react-redux';
import { ApplicationState } from 'reducers';
import { Order, Order_orders_data, Order_orders_data_order_products, Order_orders_data_coupon_data } from '@api/graphql/types';
import ButtonCancel from '@components/Form/ButtonCancel';
import { Tab, Tabs } from '@components/Tab';
import History from '../components/History';
import ReactToPrint from 'react-to-print';
import OrderProfilePrint from '../components/OrderProfilePrint';
import AssociatedLinks from '../components/AssociatedLinks';
import DataRow from '@components/DataRow';
import Price from '../components/Price';
import Notification from 'components/Notification';
import { Loading } from '@components/Loading';
import AdminOrder from '../components/AdminOrder';

type Props = {
  id?: string,
} & InjectedIntlProps & RouteComponentProps<{ id: string }>;


interface State {
  order: Order_orders_data | null;
  weight: number;
  printMode: boolean;
  loading: boolean;
}

class OrderDisplay extends React.Component<Props, State> {
  private profile: HTMLDivElement;

  public constructor(props: Props) {
    super(props);

    this.state = {
      order: null,
      weight: 0,
      printMode: false,
      loading: false,
    };
  }

  public componentDidMount() {
    this.fetchData(this.props.match.params.id);
  }

  public componentWillReceiveProps(nextProps: Props) {
    if (this.props.match.params.id !== nextProps.match.params.id) {
      this.fetchData(nextProps.match.params.id);
    }
  }

  private fetchData = async (id: string) => {
    this.setState({
      loading: true,
      order: null,
    });

    try {
      const response = await GraphQLClient.query<Order>({
        query: Queries.order,
        variables: { page: 1, count: 1, filters: { id } },
      });
      if (response.data && response.data.orders) {
        const weight = response.data.orders.data[0] ? this.getWeight(response.data.orders.data[0].order_products) : 0;
        this.setState({ order: response.data.orders.data[0], weight });
      }
    } catch {
      Notification.error(this.props.intl.formatMessage({ id: 'notification.failed' }));
    } finally {
      this.setState({ loading: false });
    }
  }

  private getWeight(orderProducts: Order_orders_data_order_products[]) {
    return orderProducts.reduce((total: number, orderProduct: Order_orders_data_order_products) => {
      return total + orderProduct.product.weight * orderProduct.default_quantity_piece;
    }, 0);
  }

  private label(id: string): string {
    return this.props.intl.formatMessage({ id: `module.order.${id}` });
  }

  private renderShipping(order: Order_orders_data) {
    return (
      <div className="col-sm-6">
        <h3>{this.label('shipping_block.title')}</h3>
        <DataRow nameWidth={6} valueWidth={6} data={{ name: this.label('shipping_name'), value: order.shipping_name }} />
        <DataRow nameWidth={6} valueWidth={6} data={{ name: this.label('shipping_address'), value: `${order.shipping_postcode} ${order.shipping_city}, ${order.shipping_street} ${order.shipping_house_number}` }} />
        <DataRow nameWidth={6} valueWidth={6} data={{ name: this.label('shipping_note'), value: order.shipping_note }} />
        <DataRow nameWidth={6} valueWidth={6} data={{ name: this.label('shipping_cost'), value: `${order.shipping_cost} Ft` }} />
      </div>
    );
  }

  private renderBilling(order: Order_orders_data) {
    return (
      <div className="col-sm-6">
        <h3>{this.label('billing_block.title')}</h3>
        <DataRow nameWidth={6} valueWidth={6} data={{ name: this.label('billing_name'), value: order.billing_name }} />
        <DataRow nameWidth={6} valueWidth={6} data={{ name: this.label('billing_address'), value: `${order.billing_postcode} ${order.billing_city}, ${order.billing_street} ${order.billing_house_number}` }} />
      </div>
    );
  }

  private renderDonation(order: Order_orders_data) {
    return (
      <div className="col-sm-6">
        <h3>{this.label('donation_block.title')}</h3>
        {(order.donation_data && order.donation_data.type)
          ? <DataRow nameWidth={6} valueWidth={6} data={{ name: this.label('donation_type'), value: `${this.label(`donation_type.${order.donation_data.type}`)}` }} />
          : null
        }
        {(order.donation_data && order.donation_data.amount)
          ? <DataRow nameWidth={6} valueWidth={6} data={{ name: this.label('donation_amount'), value: `${order.donation_data.amount} Ft` }} />
          : null
        }
      </div>
    );
  }

  private renderDetails(order: Order_orders_data) {
    let shippingMethod: JSX.Element | null = null;
    let paymentMethodName: JSX.Element | null = null;
    let paymentMethodCost: JSX.Element | null = null;
    let status: JSX.Element | null = null;
    let contactData: JSX.Element | null = null;

    if (order.shipping_method && order.shipping_method.name) {
      shippingMethod = <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('shipping_method.name.title'), value: order.shipping_method.name }} />;
    }

    if (order.payment_method && order.payment_method.name) {
      paymentMethodName = <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('payment_method.name.title'), value: order.payment_method.name }} />;
    }

    if (order.payment_method_cost) {
      paymentMethodCost = <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('payment_method_cost.title'), value: `${order.payment_method_cost} Ft` }} />;
    }

    if (order.status && order.status.title) {
      status = <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('status.title.title'), value: `${order.status.code} (${order.status.title})` }} />;
    }

    if (order.contact_data) {
      let contactName: JSX.Element | null = null;
      let contactTelephone: JSX.Element | null = null;
      let contactEmail: JSX.Element | null = null;

      if (order.contact_data.contact_name) {
        contactName = <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('contact_data.contact_name'), value: order.contact_data.contact_name }} />;
      }
      if (order.contact_data.contact_telephone) {
        contactTelephone = <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('contact_data.contact_telephone'), value: order.contact_data.contact_telephone }} />;
      }

      if (order.contact_data.contact_email) {
        contactEmail = <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('contact_data.contact_email'), value: order.contact_data.contact_email }} />;
      }

      contactData = (
        <>
          {contactName}
          {contactTelephone}
          {contactEmail}
        </>
      );
    }

    return (
      <div className="col-sm-6">
        {shippingMethod}
        {paymentMethodName}
        {paymentMethodCost}
        {status}
        {contactData}
        {(shippingMethod || paymentMethodName) && (<><br /><br /></>)}
      </div>
    );
  }

  private renderShippingMethod(order: Order_orders_data) {
    if (order.post_point) {
      return (
        <div className="col-sm-12">
          <h3>{this.label('shipping_post_point.title')}</h3>
          <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('shipping_address'), value: `${order.post_point.postcode} ${order.post_point.city}, ${order.post_point.street_name} ${order.post_point.street_type} ${order.post_point.house_number}` }} />
        </div>
      );
    }
    if (order.timetable_time_slot) {
      return (
        <div className="col-sm-12">
          <h3>{this.label('shipping_timetable.title')}</h3>
          <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('sector'), value: order.timetable_time_slot.sector.name }} />
          <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('range'), value: `${order.timetable_time_slot.range_date} ${order.timetable_time_slot.timeRange.time_from}-${order.timetable_time_slot.timeRange.time_to}` }} />
        </div>
      );
    }
    return '';
  }

  private formatInfo = (info: string | null) => {
    if (!info) { return null; }

    try {
      const parsed: {} = JSON.parse(info);
      let formattedText: string = '';

      Object.keys(parsed).forEach((key, index) => {
        formattedText = formattedText.concat(`${this.label(`additional_information.key.${key}`)}: ${parsed[key]}`);
        if (index < Object.keys(parsed).length - 1) {
          formattedText = formattedText.concat(', ');
        }
      });

      return formattedText;
    } catch {
      return info;
    }
  }

  private renderProducts(order: Order_orders_data) {
    return (
      <div className="col-sm-12">
        <h3>{this.label('products.title')}</h3>
        {order.order_products.map((orderProduct: Order_orders_data_order_products) => {
          return (
            <div className="row" key={orderProduct.product.id}>
              <div className="col-sm-3">
                <strong>{orderProduct.product.name}</strong>
              </div>
              <div className="col-sm-3">
                <Price orderProduct={orderProduct} />
              </div>
              <div className="col-sm-3">
                <strong>{orderProduct.default_quantity_piece} {orderProduct.default_quantity_name}</strong><br />
                {orderProduct.alternative_quantity_piece ? (
                  <>
                    <strong>{orderProduct.alternative_quantity_piece} {orderProduct.alternative_quantity_name}</strong><br />
                  </>
                ) : ''}
              </div>
              <div className="col-sm-3">
                {orderProduct.sliced && (
                  <p>{this.label('product.sliced')}</p>
                )}
                <strong>{this.formatInfo(orderProduct.additional_information)}</strong>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  private renderProfile() {
    if (this.state && this.state.order) {
      const { order } = this.state;
      const erpComment = order.erp_comment ? order.erp_comment.replace(/\n/g, "<br />") : '---';

      return (
        <>
          <h2>{this.label('id.title')}: {this.state.order.id} - <strong>{order.total_sum} Ft</strong> ({(this.state.weight / 1000)} kg)</h2>
          {order.admin_id && <AdminOrder adminId={order.admin_id} />}
          <div className="row">
            {this.renderShipping(order)}
            {this.renderBilling(order)}
          </div>
          {this.renderCoupon(order.coupon_data)}
          <br />
          <div className="row">
            {this.renderDonation(order)}
            {this.renderDetails(order)}
          </div>
          <div className="row">
            {this.renderShippingMethod(order)}
          </div>
          <br /><br />
          <div className="row">
            {this.renderProducts(order)}
          </div>
          <AssociatedLinks order={order} printMode={false} />
          <hr />
          <div className="row">
            <div className="col-sm-12">

              <h3>{this.label('comment.title')}</h3>

              <div className="row">
                <div className="col-sm-12">
                  {order.comment || '---'}
                </div>
              </div>
            </div>
          </div>
          <hr />
          <div className="row">
            <div className="col-sm-12">

              <h3>{this.label('erp_comment.title')}</h3>

              <div className="row">
                <div className="col-sm-12" dangerouslySetInnerHTML={{ __html: erpComment }} />
              </div>
            </div>
          </div>
          <br />
          <div className="orderDisplayProfileButtons">
            <ButtonCancel onClick={() => this.props.history.goBack()} />
            <ReactToPrint
              trigger={() => <button onClick={() => this.setState({ printMode: true })} className="btn btn-success printButton">{this.props.intl.formatMessage({ id: 'common.print' })}</button>}
              content={() => this.profile}
            />
          </div>
        </>
      );
    }
    return null;
  }
  private renderCoupon(coupon: Order_orders_data_coupon_data | null) {
    if (!coupon) {
      return '';
    }

    return (
      <>
        <hr />
        <div className="row">
          <div className="col-sm-12">
            <h3>{this.label('coupon.title')}</h3>
          </div>
          <div className="col-sm-6">
            <strong>{coupon.name} ({coupon.code})</strong>
          </div>
          <div className="col-sm-3">
            <strong>{coupon.product_discount} Ft</strong> {this.label('coupon.product_discount')}
          </div>
          <div className="col-sm-3">
            <strong>{coupon.shipping_discount} Ft</strong> {this.label('coupon.shipping_discount')}
          </div>
        </div>
        <hr />
      </>
    );
  }

  public render() {
    return (
      <div className="row orderDisplay">
        <div className="col-lg-12">
          <div className="ibox float-e-margins">
            <div className="ibox-content">
              <Tabs>
                <Tab id='profile' label={this.label('tabs.labels.profile')}>
                  {(this.state.loading) ? <Loading /> : (
                    <div className="orderDisplayProfile">
                      {this.renderProfile()}
                      <div className="orderProfilePrint" ref={(ref: HTMLDivElement) => { this.profile = ref; }}>
                        <OrderProfilePrint order={this.state.order} />
                      </div>
                    </div>
                  )}
                </Tab>
                <Tab id='history' label={this.label('tabs.labels.history')}>
                  <History id={this.props.match.params.id} />
                </Tab>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: ApplicationState, props: Props) => {
  const { id } = props.match.params;
  return { id };
};

export default compose(
  injectIntl,
  withRouter,
  connect(mapStateToProps),
)(OrderDisplay);



// WEBPACK FOOTER //
// ./src/modules/Order/pages/OrderDisplay.tsx